import { cn } from "@monorepo-apps/ui/src/lib/utils";

import {
  EDITABLE_STAGES,
  STAGE_HEADLINE,
  STAGE_LABEL,
  type Stage,
} from "@/presentation/data/architecture-components";

interface StageToggleProps {
  value: Stage;
  onChange: (next: Stage) => void;
  stages: readonly Stage[];
}

interface SegmentProps {
  stage: Stage;
  index: number;
  active: boolean;
  editable: boolean;
  onSelect: (stage: Stage) => void;
}

function Segment({ stage, index, active, editable, onSelect }: SegmentProps) {
  return (
    <button
      type="button"
      title={STAGE_HEADLINE[stage]}
      onClick={() => onSelect(stage)}
      data-active={active}
      data-editable={editable}
      className={cn(
        "font-data flex items-center gap-2 rounded-md px-3 py-1.5 text-[12px] font-medium tracking-tight transition-colors",
        active
          ? editable
            ? "bg-[oklch(0.55_0.16_55)] text-white shadow-sm"
            : "bg-[oklch(0.55_0.17_190)] text-white shadow-sm"
          : "text-muted-foreground hover:text-foreground hover:bg-[oklch(0.95_0.01_240)]"
      )}
    >
      <span
        className={cn(
          "font-data text-[10px] tracking-widest",
          active ? "text-white/70" : "text-muted-foreground/60"
        )}
      >
        S{index + 1}
      </span>
      <span>{STAGE_LABEL[stage]}</span>
      {editable ? (
        <span
          aria-hidden
          className={cn(
            "inline-flex size-1.5 shrink-0 rounded-full",
            active ? "bg-white" : "bg-[oklch(0.55_0.16_55)]"
          )}
          title="Live-edit mode"
        />
      ) : null}
    </button>
  );
}

export function StageToggle({ value, onChange, stages }: StageToggleProps) {
  const firstEditableIndex = stages.findIndex((s) => EDITABLE_STAGES.has(s));
  return (
    <div
      data-slot="stage-toggle"
      className="inline-flex items-center gap-1 rounded-lg border border-[oklch(0.91_0.008_240)] bg-white p-1"
    >
      {stages.map((stage, i) => {
        const editable = EDITABLE_STAGES.has(stage);
        return (
          <span key={stage} className="contents">
            {firstEditableIndex !== -1 && i === firstEditableIndex ? (
              <span aria-hidden className="mx-0.5 h-5 w-px bg-[oklch(0.91_0.008_240)]" />
            ) : null}
            <Segment
              stage={stage}
              index={i}
              active={value === stage}
              editable={editable}
              onSelect={onChange}
            />
          </span>
        );
      })}
    </div>
  );
}
